import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IoMenuOutline, IoCloseOutline } from 'react-icons/io5';

const links = [
  { text: 'Home', path: '/' },
  { text: 'Our Services', path: '/services' },
  { text: 'Projects Gallery', path: '/gallery' },
  { text: 'About Us', path: '/about' },
  { text: 'Contact', path: '/contact' },
];

const NavBar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Darken nav on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location]);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      {/* ── Desktop / main bar ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500
          ${scrolled
            ? 'bg-white/95 backdrop-blur-sm shadow-sm py-4'
            : 'bg-white/0 py-6'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/images/Logo.png"
              alt="Kia Kaha Construction"
              className="h-20 w-auto transition-opacity duration-300 group-hover:opacity-80"
            />
            <span
              className={`font-serif text-xl font-light tracking-wider transition-colors duration-500
                ${scrolled ? 'text-stone-800' : 'text-white'}`}
            >
              Kia Kaha
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {links.map(({ text, path }) => (
              <Link
                key={path}
                to={path}
                className={`nav-link
                  ${scrolled ? '' : '!text-white/80 hover:!text-white'}
                  ${isActive(path) ? (scrolled ? '!text-jade-500' : '!text-white') : ''}`}
              >
                {text}
                {/* Active underline */}
                {isActive(path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-jade-400" />
                )}
              </Link>
            ))}
          </nav>

          {/* Mobile hamburger */}
          <button
            className={`lg:hidden p-2 transition-colors duration-300
              ${scrolled ? 'text-stone-600 hover:text-jade-500' : 'text-white hover:text-white/70'}`}
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <IoMenuOutline className="w-7 h-7" />
          </button>
        </div>
      </header>

      {/* ── Mobile overlay ── */}
      <div
        className={`fixed inset-0 z-50 bg-stone-950 flex flex-col transition-all duration-500
          ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        {/* Close button */}
        <div className="flex justify-between items-center px-6 py-6">
          <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-3">
            <img src="/images/Logo.png" alt="Kia Kaha" className="h-16 w-auto" />
            <span className="font-serif text-xl font-light tracking-wider text-white">Kia Kaha</span>
          </Link>
          <button
            className="text-stone-400 hover:text-white transition-colors p-2"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <IoCloseOutline className="w-8 h-8" />
          </button>
        </div>

        {/* Divider */}
        <div className="h-px bg-stone-800 mx-6" />

        {/* Links */}
        <nav className="flex flex-col justify-center flex-1 px-8 gap-2">
          {links.map(({ text, path }, i) => (
            <Link
              key={path}
              to={path}
              className={`font-serif text-4xl font-light py-4 border-b border-stone-800
                transition-colors duration-200
                ${isActive(path) ? 'text-jade-400' : 'text-stone-300 hover:text-white'}`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {text}
            </Link>
          ))}
        </nav>

        {/* Footer in mobile menu */}
        <p className="text-stone-600 text-xs font-sans tracking-widest uppercase px-8 pb-8">
          Sea to Sky Corridor · Est. 2003
        </p>
      </div>
    </>
  );
};

export default NavBar;
